import type { CSSProperties, ReactElement } from 'react'
import { cn } from '../../lib/cn'

export interface SkeletonProps {
  className?: string
  width?: string | number
  height?: string | number
  shape?: 'line' | 'block' | 'circle'
  style?: CSSProperties
}

/**
 * A placeholder shaped like the content that is coming. Under `prefers-reduced-motion` the
 * shimmer stops and the block stays a flat sunken fill.
 */
export const Skeleton = ({
  className,
  width,
  height,
  shape = 'line',
  style,
}: SkeletonProps): ReactElement => (
  <span
    aria-hidden="true"
    className={cn(
      'anim-shimmer block',
      shape === 'circle' ? 'rounded-full' : shape === 'block' ? 'rounded-md' : 'rounded-sm',
      shape === 'line' && !height && 'h-3',
      className,
    )}
    style={{ width, height, ...style }}
  />
)

export interface SkeletonTextProps {
  lines?: number
  className?: string
}

/** A paragraph of placeholder lines, the last one short so it reads as text. */
export const SkeletonText = ({ lines = 3, className }: SkeletonTextProps): ReactElement => (
  <span className={cn('flex flex-col gap-2', className)} aria-hidden="true">
    {Array.from({ length: lines }, (_, index) => (
      <Skeleton key={index} width={index === lines - 1 ? '60%' : '100%'} />
    ))}
  </span>
)
